import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api, adminApi } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const AdminGate = () => {
  const { adminGateOpen, setAdminGateOpen } = useApp();
  const navigate = useNavigate();
  const [exists, setExists] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "MOVA Admin" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (adminGateOpen) {
      api.get("/admin/exists").then((r) => setExists(r.data.exists)).catch(() => setExists(true));
    }
  }, [adminGateOpen]);

  const submit = async () => {
    if (!form.email || !form.password) {
      toast.error("Email and password required");
      return;
    }
    setLoading(true);
    try {
      const endpoint = exists ? "/admin/login" : "/admin/setup";
      const res = await api.post(endpoint, form);
      localStorage.setItem("mova_admin_token", res.data.token);
      toast.success(exists ? "Welcome back, admin" : "Admin account created");
      setAdminGateOpen(false);
      navigate("/admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not authenticate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={adminGateOpen} onOpenChange={setAdminGateOpen}>
      <DialogContent className="sm:max-w-md bg-[#0d0d12] border border-[rgba(212,175,55,0.2)] text-white" data-testid="admin-gate-modal">
        <DialogTitle className="sr-only">Admin access</DialogTitle>
        <DialogDescription className="sr-only">Set up or log into the MOVA admin center.</DialogDescription>
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full border border-amber-400/40 flex items-center justify-center text-champagne gold-glow"><ShieldCheck size={22} /></div>
        </div>
        <h3 className="font-serif text-2xl text-center text-champagne">{exists === false ? "Set up Admin Center" : "Admin Center"}</h3>
        <p className="text-center text-slate-400 text-sm mt-2">
          {exists === false ? "Create your secret admin account to manage MOVA." : "Sign in to manage announcements, listings, payments & orders."}
        </p>

        <div className="space-y-3 mt-6">
          {exists === false && (
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Admin name" data-testid="admin-name-input" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white focus:outline-none" />
          )}
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Admin email" data-testid="admin-email-input" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none" />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Password" data-testid="admin-password-input" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none" />
          <button onClick={submit} disabled={loading || exists === null} data-testid="admin-submit-btn" className="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] py-3 font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
            {loading ? <Loader2 className="animate-spin" size={18} /> : exists === false ? "Create admin" : "Sign in"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
